<template>
  <div class="lesson-preparation-page">
    <!-- 背景装饰 -->
    <div class="bg-decoration">
      <div class="bg-circle bg-circle-1"></div>
      <div class="bg-circle bg-circle-2"></div>
      <div class="bg-circle bg-circle-3"></div>
    </div>

    <!-- 头部 -->
    <header class="header">
      <div class="header-container">
        <div class="logo">
          <div class="logo-icon">AI</div>
          <div class="logo-text">智能备课系统</div>
          <span class="version-badge">V1.0</span>
        </div>
        <div class="user-info">
          <span>试点建设账号：节点1/4</span>
          <span>|</span>
          <span>中国-东盟建筑工程技术专业</span>
        </div>
      </div>
    </header>

    <!-- 主容器 -->
    <div class="main-container">
      <!-- Hero 区域 -->
      <section class="hero-section">
        <div class="hero-badge">
          <span class="hero-badge-dot"></span>
          <span>中国-东盟建筑工程技术专业AI智能备课</span>
        </div>

        <h1 class="hero-title">AI智能备课系统</h1>
        <p class="hero-subtitle">基于中国-东盟建筑工程技术专业知识库，为教师提供高效便捷的备课解决方案</p>
      </section>

      <!-- 课程导航 -->
      <section class="course-nav-section">
        <h2 class="nav-title">
          <span>📚</span>
          <span>课程分类导航</span>
        </h2>
        <nav class="course-nav">
          <div v-for="nav in courseCategories" :key="nav.value" class="nav-item"
            :class="{ active: activeCategory === nav.value }" @click="selectCategory(nav.value)">
            <div class="nav-icon">{{ nav.icon }}</div>
            <div>
              <div>{{ nav.label }}</div>
              <small style="opacity: 0.7;">{{ nav.desc }}</small>
            </div>
          </div>
        </nav>
      </section>

      <!-- 主内容卡片 -->
      <div class="content-card">
        <div class="content-header">
          <h1 class="content-title">开始备课</h1>
          <p class="content-subtitle">选择课程、上传教材，3分钟完成备课资源生成</p>
        </div>

        <div class="content-body">
          <div class="step-form">
            <!-- Step 1: 选择课程 -->
            <div class="form-step" :class="{ active: currentStep === 1, completed: currentStep > 1 }">
              <div class="step-header">
                <div class="step-number">1</div>
                <h2 class="step-title">选择课程</h2>
              </div>

              <div class="form-group">
                <label class="form-label">课程分类</label>
                <select class="form-select" v-model="activeCategory" @change="loadCourses">
                  <option value="basic">专业基础课程</option>
                  <option value="core">专业核心课程</option>
                  <option value="extended">专业拓展课程</option>
                  <option value="practice">实训课程</option>
                </select>
              </div>

              <div class="course-grid">
                <div v-for="course in currentCourses" :key="course.id" class="course-card"
                  :class="{ selected: selectedCourse && selectedCourse.id === course.id }"
                  @click="selectCourse(course)">
                  <div class="course-name">{{ course.name }}</div>
                  <div class="course-code">{{ course.code }}</div>
                  <div class="course-desc">{{ course.desc }}</div>
                </div>
              </div>
            </div>

            <!-- Step 2: 上传教材 -->
            <div class="form-step" :class="{ active: currentStep === 2, completed: currentStep > 2 }">
              <div class="step-header">
                <div class="step-number">2</div>
                <h2 class="step-title">上传教材</h2>
              </div>

              <!-- <div class="form-group">
                <label class="form-label">选择教材来源</label>
                <select class="form-select" v-model="materialSource">
                  <option value="custom">自定义上传教材</option>
                  <option value="default">使用系统默认教材</option>
                </select>
              </div> -->

              <div v-if="materialSource === 'custom'" class="upload-area" :class="{ dragover: isDragging }"
                @click="$refs.fileInput.click()" @dragover.prevent="isDragging = true"
                @dragleave.prevent="isDragging = false" @drop.prevent="handleDrop">
                <div class="upload-icon">📄</div>
                <div class="upload-text">点击上传或拖拽文件到此区域</div>
                <div class="upload-hint">支持 PDF、Word、PPT 格式，单个文件不超过 50MB</div>
                <button class="upload-button" @click.stop="$refs.fileInput.click()">
                  选择文件
                </button>
                <input ref="fileInput" type="file" style="display: none;" accept=".pdf,.doc,.docx,.ppt,.pptx" multiple
                  @change="handleFileSelect">
              </div>

              <div class="file-list" v-if="uploadedFiles.length > 0">
                <div v-for="(file, index) in uploadedFiles" :key="index" class="file-item">
                  <div class="file-icon">📄</div>
                  <div class="file-info">
                    <div class="file-name">{{ file.name }}</div>
                    <div class="file-size">{{ formatFileSize(file.size) }}</div>
                  </div>
                  <button class="file-remove" @click="removeFile(index)">×</button>
                </div>
              </div>
            </div>

            <!-- Step 3: 生成资源 -->
            <div class="form-step" :class="{ active: currentStep === 3, completed: currentStep > 3 }">
              <div class="step-header">
                <div class="step-number">3</div>
                <h2 class="step-title">生成教学资源</h2>
              </div>

              <div class="generation-area">
                <div class="generation-status">{{ generationStatus }}</div>

                <div class="progress-container">
                  <div class="progress-bar">
                    <div class="progress-fill" :style="{ width: generationProgress + '%' }"></div>
                  </div>
                  <div class="progress-text">
                    <span>生成进度</span>
                    <span>{{ Math.floor(generationProgress) }}%</span>
                  </div>
                </div>

                <div class="generation-steps">
                  <div v-for="(step, index) in generationSteps" :key="index" class="generation-step" :class="{
                    completed: index < currentGenerationStep,
                    current: index === currentGenerationStep
                  }">
                    <span class="step-icon">{{ step.icon }}</span>
                    <span class="step-text">{{ step.text }}</span>
                  </div>
                </div>
              </div>
            </div>

            <!-- Step 4: 下载资源 -->
            <div class="form-step" :class="{ active: currentStep === 4 }">
              <div class="step-header">
                <div class="step-number">4</div>
                <h2 class="step-title">下载资源</h2>
              </div>

              <div class="results-grid">
                <div v-for="result in generatedResults" :key="result.title" class="result-card">
                  <span class="result-icon">{{ result.icon }}</span>
                  <h3 class="result-title">{{ result.title }}</h3>
                  <p class="result-desc">{{ result.desc }}</p>
                  <div class="result-actions">
                    <button class="action-button primary" @click="downloadFile(result)">
                      <span>📥</span>
                      下载 {{ result.format }}
                    </button>
                    <button class="action-button secondary" @click="previewFile(result)">
                      <span>👁️</span>
                      预览
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div class="form-actions">
            <button class="btn btn-secondary" v-show="currentStep > 1" @click="previousStep">
              ← 上一步
            </button>
            <button class="btn btn-primary" v-show="currentStep < 4" :disabled="!canProceed" @click="nextStep">
              {{ nextButtonText }}
            </button>
            <button class="btn btn-primary" v-show="currentStep === 4" @click="resetForm">
              重新开始
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { lessonAPI } from '../../api/lessonAPI'
import { fileUtils } from '../../utils/ileHandler'
import { Message } from 'element-ui'
export default {
  name: 'LessonPreparation',
  data() {
    return {
      currentStep: 1,
      activeCategory: 'basic',
      selectedCourse: null,
      materialSource: 'custom',
      uploadedFiles: [],
      isDragging: false,
      generationProgress: 0,
      generationStatus: '准备开始生成...',
      currentGenerationStep: -1,
      isGenerating: false,
      generatedResults: [],

      // 课程分类
      courseCategories: [
        { value: 'basic', label: '专业基础课程', icon: '🏗️', desc: '8门核心基础课程' },
        { value: 'core', label: '专业核心课程', icon: '🎯', desc: '6门专业核心课程' },
        { value: 'extended', label: '专业拓展课程', icon: '📈', desc: '13门拓展选修课程' },
        { value: 'practice', label: '实训课程', icon: '🔬', desc: '8个实训项目' }
      ],

      // 生成步骤
      generationSteps: [
        { icon: '🔍', text: '解析教材内容...' },
        { icon: '🧠', text: 'AI智能分析...' },
        { icon: '📊', text: '生成PPT课件...' },
        { icon: '📋', text: '生成课程标准...' },
        { icon: '📝', text: '生成教学教案...' },
        { icon: '✅', text: '完成资源打包...' }
      ],

      // 课程数据
      courseData: {
        basic: [
          { id: 1, name: '建筑材料', code: '010101', desc: '掌握各种建筑材料的性能、规格及应用技术' },
          { id: 2, name: '建筑制图与CAD', code: '010102', desc: '建筑工程图纸绘制与计算机辅助设计技术' },
          { id: 3, name: '建筑构造', code: '010103', desc: '建筑物各部分构造原理与技术要求' },
          { id: 4, name: '建筑工程测量', code: '010104', desc: '工程测量理论、方法与测量仪器使用' },
          { id: 5, name: '建筑力学', code: '010105', desc: '建筑结构力学基础理论与计算方法' },
          { id: 6, name: '建筑结构', code: '010106', desc: '建筑结构设计原理与构造要求' },
          { id: 7, name: '工程岩土', code: '010107', desc: '工程地质条件分析与岩土工程技术' },
          { id: 8, name: '建筑设备与识图', code: '010108', desc: '建筑给排水、电气、暖通设备基础与识图' }
        ],
        core: [
          { id: 9, name: '建筑施工技术', code: '010201', desc: '各种建筑施工工艺与技术要点' },
          { id: 10, name: '建筑施工组织', code: '010202', desc: '施工组织设计与进度计划管理' },
          { id: 11, name: '建筑工程质量与安全管理', code: '010203', desc: '工程质量控制与安全生产管理体系' },
          { id: 12, name: '建筑工程计量与计价', code: '010204', desc: '建筑工程计量规则与造价计算方法' },
          { id: 13, name: '建筑工程资料管理', code: '010205', desc: '工程技术资料编制与管理规范' },
          { id: 14, name: '建筑信息模型应用', code: '010206', desc: 'BIM技术在建筑工程中的应用实践' }
        ],
        extended: [
          { id: 15, name: '建筑节能', code: '010301', desc: '绿色建筑与建筑节能技术应用' },
          { id: 16, name: '建筑抗震', code: '010302', desc: '建筑抗震设计原理与构造措施' },
          { id: 17, name: '工程建设监理概论', code: '010303', desc: '工程监理理论基础与实务操作' },
          { id: 18, name: 'BIM建模', code: '010304', desc: 'BIM建模软件应用与三维建模技术' },
          { id: 19, name: '工程建设法规', code: '010305', desc: '建筑工程相关法律法规与标准规范' },
          { id: 20, name: '装配式建筑施工', code: '010306', desc: '预制装配式建筑设计与施工技术' },
          { id: 21, name: '智能建造概论', code: '010307', desc: '智能建造技术发展与应用前景' },
          { id: 22, name: '招投标与合同管理', code: '010308', desc: '工程招标投标程序与合同管理实务' },
          { id: 23, name: '地理信息系统', code: '010309', desc: 'GIS技术在建筑工程中的应用' },
          { id: 24, name: '智能测量技术', code: '010310', desc: '现代测量技术与智能化测量设备' },
          { id: 25, name: '智能检测技术', code: '010311', desc: '建筑工程智能检测技术与设备应用' },
          { id: 26, name: '建筑工程项目管理', code: '010312', desc: '建筑工程项目全过程管理理论与实践' },
          { id: 27, name: '施工用电技术', code: '010313', desc: '建筑施工现场临时用电技术与安全' }
        ],
        practice: [
          { id: 28, name: '建筑施工工艺实训', code: '010401', desc: '砌筑、抹灰、钢筋绑扎等施工工艺实操训练' },
          { id: 29, name: '装配式混凝土建筑施工实训', code: '010402', desc: '装配式混凝土构件制作与安装实训' },
          { id: 30, name: '建筑工程造价实训', code: '010403', desc: '工程量计算与造价软件应用实训' },
          { id: 31, name: '建筑信息模型应用实训', code: '010404', desc: 'BIM软件建模与应用综合实训' },
          { id: 32, name: '建筑施工技术实训', code: '010405', desc: '建筑施工技术综合实操训练' },
          { id: 33, name: '建筑施工管理实训', code: '010406', desc: '施工现场管理综合实践训练' },
          { id: 34, name: '认识实习', code: '010407', desc: '建筑施工现场认识与岗位了解实习' },
          { id: 35, name: '岗位实习', code: '010408', desc: '建筑工程相关岗位顶岗实习' }
        ],
        generationTaskId: null,
        progressTimer: null
      }
    }
  },

  computed: {
    currentCourses() {
      return this.courseData[this.activeCategory] || []
    },

    canProceed() {
      switch (this.currentStep) {
        case 1:
          return !!this.selectedCourse
        case 2:
          return this.materialSource === 'default' || this.uploadedFiles.length > 0
        case 3:
          return this.generationProgress >= 100
        default:
          return true
      }
    },

    nextButtonText() {
      if (this.currentStep === 2 && this.isGenerating) {
        return '生成中...'
      }
      return '下一步 →'
    }
  },

  methods: {
    selectCategory(category) {
      this.activeCategory = category
    },

    loadCourses() {
      // 切换分类时更新导航激活状态
      this.selectCategory(this.activeCategory)
    },

    selectCourse(course) {
      this.selectedCourse = course
    },

    handleFileSelect(event) {
      const files = Array.from(event.target.files)
      this.addFiles(files)
      console.log(event, 11223)
    },

    handleDrop(event) {
      this.isDragging = false
      const files = Array.from(event.dataTransfer.files)
      this.addFiles(files)
    },

    addFiles(files) {
      const allowedTypes = {
        mimeTypes: [
          'application/pdf',
          'application/msword',
          'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
          'application/vnd.ms-powerpoint',
          'application/vnd.openxmlformats-officedocument.presentationml.presentation'
        ],
        extensions: ['pdf', 'doc', 'docx', 'ppt', 'pptx']
      }

      const maxSize = 50 * 1024 * 1024 // 50MB

      files.forEach(file => {
        // 验证文件类型
        if (!fileUtils.validateFileType(file, allowedTypes)) {
          Message.error(`文件 ${file.name} 格式不支持`)
          return
        }

        // 验证文件大小
        if (!fileUtils.validateFileSize(file, maxSize)) {
          Message.error(`文件 ${file.name} 超过50MB限制`)
          return
        }

        this.uploadedFiles.push(file)
      })
    },

    validateFile(file) {
      const allowedTypes = [
        'application/pdf',
        'application/msword',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        'application/vnd.ms-powerpoint',
        'application/vnd.openxmlformats-officedocument.presentationml.presentation'
      ]

      if (!allowedTypes.includes(file.type)) {
        alert('不支持的文件格式，请上传 PDF、Word 或 PPT 文件')
        return false
      }

      if (file.size > 50 * 1024 * 1024) {
        alert('文件大小不能超过 50MB')
        return false
      }

      return true
    },

    removeFile(index) {
      this.uploadedFiles.splice(index, 1)
    },

    formatFileSize(bytes) {
      if (bytes === 0) return '0 Bytes'
      const k = 1024
      const sizes = ['Bytes', 'KB', 'MB', 'GB']
      const i = Math.floor(Math.log(bytes) / Math.log(k))
      return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
    },

    async nextStep() {
      if (this.currentStep === 2) {
        // 开始生成
        this.isGenerating = true
        await this.startGeneration()
        return
      }

      if (this.currentStep < 4) {
        this.currentStep++
      }
    },

    previousStep() {
      if (this.currentStep > 1) {
        this.currentStep--
      }
    },

    async startGeneration() {
      if (!this.selectedCourse) {
        Message.error('请先选择课程')
        return
      }

      if (this.uploadedFiles.length === 0 && this.materialSource === 'custom') {
        Message.error('请先上传教材文件')
        return
      }

      this.currentStep = 3
      this.generationProgress = 0
      this.currentGenerationStep = 0
      this.generationStatus = '正在上传教材文件...'

      try {
        let materialIds = []

        // 如果是自定义上传，先上传文件
        if (this.materialSource === 'custom' && this.uploadedFiles.length > 0) {
          // 批量上传
          const uploadRes = await lessonAPI.uploadMultipleMaterials(
            this.uploadedFiles,
            this.selectedCourse.id
          )

          if (uploadRes.success && uploadRes.data?.materialIds) {
            materialIds = uploadRes.data.materialIds
            this.generationStatus = '教材上传成功，开始分析...'
          } else {
            throw new Error('文件上传失败')
          }
        } else if (this.materialSource === 'default') {
          // 使用默认教材
          const defaultRes = await lessonAPI.useDefaultMaterial(this.selectedCourse.id)
          if (defaultRes.success && defaultRes.data?.materialIds) {
            materialIds = defaultRes.data.materialIds
          }
        }

        // 开始生成资源
        this.generationStatus = '正在生成教学资源...'
        const generateRes = await lessonAPI.generateResources({
          courseId: this.selectedCourse.id,
          materialIds: materialIds
        })

        if (generateRes.success && generateRes.data?.taskId) {
          this.generationTaskId = generateRes.data.taskId

          // 开始轮询进度
          this.pollGenerationProgress()
        } else {
          throw new Error('生成任务创建失败')
        }
      } catch (error) {
        console.error('生成资源失败:', error)
        Message.error(error.message || '生成资源失败，请重试')
        this.isGenerating = false
        this.currentStep = 2 // 返回上一步
      }
    },
    // 轮询生成进度
    async pollGenerationProgress() {
      if (!this.generationTaskId) return

      this.progressTimer = setInterval(async () => {
        try {
          const progressRes = await lessonAPI.getGenerationProgress(this.generationTaskId)

          if (progressRes.success) {
            const { progress, status, currentStep, resources } = progressRes.data

            // 更新进度
            this.generationProgress = progress || 0
            this.generationStatus = status || '处理中...'

            // 更新当前步骤
            if (currentStep !== undefined && currentStep !== this.currentGenerationStep) {
              this.currentGenerationStep = currentStep
            }

            // 检查是否完成
            if (progress >= 100) {
              clearInterval(this.progressTimer)
              this.progressTimer = null
              this.isGenerating = false

              // 生成完成，准备下载
              if (resources && resources.length > 0) {
                this.prepareDownloadResults(resources)
                this.currentStep = 4
                Message.success('教学资源生成完成！')
              }
            }
          }
        } catch (error) {
          console.error('获取进度失败:', error)
          // 继续轮询，不中断
        }
      }, 2000) // 每2秒查询一次
    },
    // 准备下载结果
  prepareDownloadResults(resources) {
    this.generatedResults = resources.map(resource => {
      let icon, title, format
      
      switch (resource.type) {
        case 'ppt':
          icon = '📊'
          title = 'PPT课件'
          format = 'PPTX'
          break
        case 'standard':
          icon = '📋'
          title = '课程标准'
          format = 'PDF'
          break
        case 'plan':
          icon = '📝'
          title = '课程教案'
          format = 'DOCX'
          break
        default:
          icon = '📄'
          title = resource.name || '教学资源'
          format = resource.format || 'PDF'
      }
      
      return {
        ...resource,
        icon,
        title,
        format,
        desc: resource.description || `${title}已生成，可以下载使用`
      }
    })
  },

    generateResults() {
      this.generatedResults = [
        {
          icon: '📊',
          title: 'PPT课件',
          desc: '包含完整的教学内容，精美的设计模板，适合课堂教学使用',
          format: 'PPTX'
        },
        {
          icon: '📋',
          title: '课程标准',
          desc: '符合教育规范的课程标准文档，明确教学目标和要求',
          format: 'PDF'
        },
        {
          icon: '📝',
          title: '课程教案',
          desc: '详细的教学设计方案，包含教学过程和重难点分析',
          format: 'DOCX'
        }
      ]
    },

// 下载文件
  async downloadFile(result) {
    try {
      Message.info('正在准备下载...')
      const filename = `${result.title}_${this.selectedCourse.name}.${result.format.toLowerCase()}`
      
      await lessonAPI.downloadResource(result.id || result.resourceId, filename)
      Message.success('下载成功！')
    } catch (error) {
      console.error('下载失败:', error)
      Message.error('下载失败，请重试')
    }
  },

  // 预览文件
  async previewFile(result) {
    try {
      Message.info('正在加载预览...')
      const previewRes = await lessonAPI.previewResource(result.id || result.resourceId)
      
      if (previewRes.success && previewRes.data?.previewUrl) {
        // 在新窗口打开预览
        window.open(previewRes.data.previewUrl, '_blank')
      } else {
        Message.warning('该文件暂不支持预览，请下载后查看')
      }
    } catch (error) {
      console.error('预览失败:', error)
      Message.error('预览失败，请下载后查看')
    }
  },

 // 重置表单
  resetForm() {
    // 清理定时器
    if (this.progressTimer) {
      clearInterval(this.progressTimer)
      this.progressTimer = null
    }

    // 重置所有状态
    this.currentStep = 1
    this.selectedCourse = null
    this.uploadedFiles = []
    this.generationProgress = 0
    this.currentGenerationStep = -1
    this.generationStatus = '准备开始生成...'
    this.generatedResults = []
    this.materialSource = 'custom'
    this.isGenerating = false
    this.generationTaskId = null
  },
  },
   // 组件销毁时清理
  beforeDestroy() {
    if (this.progressTimer) {
      clearInterval(this.progressTimer)
    }
  }
}
</script>

<style scoped>
.lesson-preparation-page {
  --primary: #2E5CE6;
  --primary-dark: #1E3A8A;
  --primary-light: #60A5FA;
  --secondary: #10B981;
  --accent: #06B6D4;
  --purple: #8B5CF6;
  --pink: #EC4899;
  --orange: #F59E0B;
  --asean: #D97706;
  --china-red: #DC2626;
  --success: #10B981;
  --warning: #F59E0B;
  --danger: #EF4444;
  --bg-white: #FFFFFF;
  --bg-gray-50: #F9FAFB;
  --bg-gray-100: #F3F4F6;
  --bg-gray-200: #E5E7EB;
  --bg-gradient: linear-gradient(135deg, #EBF5FF 0%, #F0F9FF 50%, #FFF7ED 100%);
  --text-primary: #111827;
  --text-secondary: #6B7280;
  --text-tertiary: #9CA3AF;
  --border-light: #E5E7EB;
  --border-medium: #D1D5DB;
  --shadow-xs: 0 1px 2px 0 rgba(0, 0, 0, 0.05);
  --shadow-sm: 0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06);
  --shadow-md: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
  --shadow-lg: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05);
  --shadow-xl: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
  --shadow-2xl: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
  --radius-sm: 8px;
  --radius-md: 12px;
  --radius-lg: 16px;
  --radius-xl: 24px;
  --radius-2xl: 32px;
  --transition-base: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);

  font-family: -apple-system, BlinkMacSystemFont, 'SF Pro Display', 'Segoe UI', 'PingFang SC', 'Microsoft YaHei', sans-serif;
  background: var(--bg-gradient);
  color: var(--text-primary);
  line-height: 1.6;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  min-height: 100vh;
  position: relative;
  overflow-x: hidden;
}

/* Background decoration */
.bg-decoration {
  position: fixed;
  width: 100%;
  height: 100%;
  top: 0;
  left: 0;
  z-index: -1;
  overflow: hidden;
}

.bg-circle {
  position: absolute;
  border-radius: 50%;
  opacity: 0.03;
}

.bg-circle-1 {
  width: 600px;
  height: 600px;
  background: var(--primary);
  top: -300px;
  right: -200px;
}

.bg-circle-2 {
  width: 400px;
  height: 400px;
  background: var(--asean);
  bottom: -200px;
  left: -100px;
}

.bg-circle-3 {
  width: 300px;
  height: 300px;
  background: var(--china-red);
  top: 50%;
  left: 10%;
}

/* Header */
.header {
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(20px);
  border-bottom: 1px solid var(--border-light);
  padding: 16px 0;
  position: sticky;
  top: 0;
  z-index: 100;
}

.header-container {
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 20px;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.logo {
  display: flex;
  align-items: center;
  gap: 12px;
  font-size: 24px;
  font-weight: 800;
}

.logo-icon {
  width: 48px;
  height: 48px;
  background: linear-gradient(135deg, var(--china-red) 0%, var(--asean) 100%);
  border-radius: var(--radius-md);
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 20px;
}

.logo-text {
  background: linear-gradient(135deg, var(--china-red) 0%, var(--primary) 50%, var(--asean) 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.user-info {
  display: flex;
  align-items: center;
  gap: 16px;
  font-size: 14px;
  color: var(--text-secondary);
}

.version-badge {
  background: linear-gradient(135deg, var(--primary) 0%, var(--asean) 100%);
  color: white;
  padding: 4px 12px;
  border-radius: 100px;
  font-size: 12px;
  font-weight: 700;
}

/* Main Container */
.main-container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 40px 20px;
}

/* Hero Section */
.hero-section {
  text-align: center;
  margin-bottom: 60px;
}

.hero-badge {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: white;
  padding: 8px 20px;
  border-radius: 100px;
  font-size: 14px;
  font-weight: 600;
  color: var(--primary);
  box-shadow: var(--shadow-md);
  margin-bottom: 24px;
}

.hero-badge-dot {
  width: 8px;
  height: 8px;
  background: var(--success);
  border-radius: 50%;
  animation: pulse 2s infinite;
}

@keyframes pulse {
  0% {
    opacity: 1;
    transform: scale(1);
  }

  50% {
    opacity: 0.5;
    transform: scale(1.2);
  }

  100% {
    opacity: 1;
    transform: scale(1);
  }
}

.hero-title {
  font-size: 48px;
  font-weight: 800;
  line-height: 1.1;
  margin-bottom: 16px;
  background: linear-gradient(135deg, var(--china-red) 0%, var(--primary) 50%, var(--asean) 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  letter-spacing: -0.02em;
}

.hero-subtitle {
  font-size: 18px;
  color: var(--text-secondary);
  max-width: 600px;
  margin: 0 auto;
  line-height: 1.6;
}

/* Course Navigation */
.course-nav-section {
  margin-bottom: 40px;
}

.nav-title {
  font-size: 24px;
  font-weight: 700;
  margin-bottom: 20px;
  color: var(--text-primary);
  text-align: center;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
}

.course-nav {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 16px;
  max-width: 1000px;
  margin: 0 auto;
}

.nav-item {
  background: white;
  padding: 20px;
  border-radius: var(--radius-lg);
  cursor: pointer;
  transition: var(--transition-base);
  display: flex;
  align-items: center;
  gap: 16px;
  font-size: 16px;
  font-weight: 600;
  border: 2px solid var(--border-light);
  box-shadow: var(--shadow-sm);
}

.nav-item:hover {
  transform: translateY(-4px);
  box-shadow: var(--shadow-lg);
  border-color: var(--primary-light);
}

.nav-item.active {
  background: linear-gradient(135deg, rgba(46, 92, 230, 0.1) 0%, rgba(217, 119, 6, 0.1) 100%);
  color: var(--primary);
  border-color: var(--primary);
  transform: translateY(-4px);
  box-shadow: var(--shadow-lg);
}

.nav-icon {
  width: 48px;
  height: 48px;
  background: var(--bg-gray-100);
  border-radius: var(--radius-md);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 24px;
  flex-shrink: 0;
}

.nav-item.active .nav-icon {
  background: linear-gradient(135deg, var(--primary) 0%, var(--asean) 100%);
  color: white;
}

/* Main Content Card */
.content-card {
  background: white;
  border-radius: var(--radius-2xl);
  box-shadow: var(--shadow-xl);
  overflow: hidden;
  border: 1px solid var(--border-light);
}

.content-header {
  background: linear-gradient(135deg, var(--china-red) 0%, var(--primary) 50%, var(--asean) 100%);
  padding: 32px;
  color: white;
  text-align: center;
}

.content-title {
  font-size: 32px;
  font-weight: 800;
  margin-bottom: 8px;
}

.content-subtitle {
  font-size: 16px;
  opacity: 0.9;
}

.content-body {
  padding: 32px;
}

/* Step Form */
.step-form {
  display: flex;
  flex-direction: column;
  gap: 32px;
}

.form-step {
  opacity: 0.6;
  pointer-events: none;
  transition: var(--transition-base);
}

.form-step.active {
  opacity: 1;
  pointer-events: auto;
}

.form-step.completed {
  opacity: 0.8;
}

.step-header {
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 24px;
}

.step-number {
  width: 40px;
  height: 40px;
  background: var(--bg-gray-200);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 18px;
  color: var(--text-secondary);
  transition: var(--transition-base);
}

.form-step.active .step-number,
.form-step.completed .step-number {
  background: linear-gradient(135deg, var(--primary) 0%, var(--asean) 100%);
  color: white;
}

.step-title {
  font-size: 20px;
  font-weight: 700;
  color: var(--text-primary);
}

.form-group {
  margin-bottom: 24px;
}

.form-label {
  display: block;
  font-size: 16px;
  font-weight: 600;
  margin-bottom: 8px;
  color: var(--text-primary);
}

.form-select,
.form-input {
  width: 100%;
  padding: 16px;
  border: 2px solid var(--border-light);
  border-radius: var(--radius-md);
  font-size: 16px;
  font-family: inherit;
  transition: var(--transition-base);
  background: white;
}

.form-select:focus,
.form-input:focus {
  outline: none;
  border-color: var(--primary);
  box-shadow: 0 0 0 3px rgba(46, 92, 230, 0.1);
}

.course-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 16px;
  margin-top: 16px;
}

.course-card {
  background: var(--bg-gray-50);
  border: 2px solid var(--border-light);
  border-radius: var(--radius-md);
  padding: 20px;
  cursor: pointer;
  transition: var(--transition-base);
  position: relative;
}

.course-card:hover {
  transform: translateY(-2px);
  box-shadow: var(--shadow-md);
}

.course-card.selected {
  border-color: var(--primary);
  background: rgba(46, 92, 230, 0.05);
}

.course-card.selected::after {
  content: '✓';
  position: absolute;
  top: 12px;
  right: 12px;
  width: 24px;
  height: 24px;
  background: var(--primary);
  color: white;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  font-weight: 700;
}

.course-name {
  font-size: 16px;
  font-weight: 600;
  margin-bottom: 8px;
  color: var(--text-primary);
}

.course-code {
  font-size: 14px;
  color: var(--text-secondary);
  margin-bottom: 12px;
}

.course-desc {
  font-size: 14px;
  color: var(--text-secondary);
  line-height: 1.4;
}

/* File Upload */
.upload-area {
  border: 2px dashed var(--border-medium);
  border-radius: var(--radius-lg);
  padding: 40px;
  text-align: center;
  background: var(--bg-gray-50);
  transition: var(--transition-base);
  cursor: pointer;
  position: relative;
  overflow: hidden;
}

.upload-area:hover {
  border-color: var(--primary);
  background: rgba(46, 92, 230, 0.02);
}

.upload-area.dragover {
  border-color: var(--primary);
  background: rgba(46, 92, 230, 0.05);
  transform: scale(1.02);
}

.upload-icon {
  font-size: 48px;
  color: var(--text-tertiary);
  margin-bottom: 16px;
}

.upload-text {
  font-size: 18px;
  font-weight: 600;
  color: var(--text-primary);
  margin-bottom: 8px;
}

.upload-hint {
  font-size: 14px;
  color: var(--text-secondary);
  margin-bottom: 20px;
}

.upload-button {
  background: linear-gradient(135deg, var(--primary) 0%, var(--asean) 100%);
  color: white;
  padding: 12px 24px;
  border: none;
  border-radius: var(--radius-md);
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: var(--transition-base);
}

.upload-button:hover {
  transform: translateY(-2px);
  box-shadow: 0 10px 20px rgba(46, 92, 230, 0.3);
}

.file-list {
  margin-top: 20px;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.file-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  background: white;
  border-radius: var(--radius-md);
  border: 1px solid var(--border-light);
}

.file-icon {
  font-size: 20px;
  color: var(--primary);
}

.file-info {
  flex: 1;
}

.file-name {
  font-size: 14px;
  font-weight: 600;
  color: var(--text-primary);
}

.file-size {
  font-size: 12px;
  color: var(--text-secondary);
}

.file-remove {
  background: var(--danger);
  color: white;
  border: none;
  border-radius: 50%;
  width: 24px;
  height: 24px;
  cursor: pointer;
  font-size: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
}

/* Generation Process */
.generation-area {
  background: var(--bg-gray-50);
  border-radius: var(--radius-lg);
  padding: 32px;
  text-align: center;
}

.generation-status {
  font-size: 18px;
  font-weight: 600;
  margin-bottom: 20px;
  color: var(--text-primary);
}

.progress-container {
  margin-bottom: 24px;
}

.progress-bar {
  width: 100%;
  height: 12px;
  background: var(--bg-gray-200);
  border-radius: 6px;
  overflow: hidden;
  margin-bottom: 8px;
}

.progress-fill {
  height: 100%;
  background: linear-gradient(90deg, var(--primary) 0%, var(--asean) 100%);
  border-radius: 6px;
  transition: width 0.5s ease;
  width: 0%;
}

.progress-text {
  font-size: 14px;
  color: var(--text-secondary);
  display: flex;
  justify-content: space-between;
}

.generation-steps {
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin-top: 24px;
  text-align: left;
}

.generation-step {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  background: white;
  border-radius: var(--radius-md);
  border-left: 4px solid var(--bg-gray-200);
  transition: var(--transition-base);
}

.generation-step.completed {
  border-left-color: var(--success);
}

.generation-step.current {
  border-left-color: var(--primary);
  background: rgba(46, 92, 230, 0.05);
}

.step-icon {
  font-size: 16px;
  color: var(--text-secondary);
}

.generation-step.completed .step-icon {
  color: var(--success);
}

.generation-step.current .step-icon {
  color: var(--primary);
}

.step-text {
  font-size: 14px;
  color: var(--text-primary);
}

/* Results */
.results-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 20px;
  margin-top: 24px;
}

.result-card {
  background: white;
  border: 1px solid var(--border-light);
  border-radius: var(--radius-lg);
  padding: 24px;
  transition: var(--transition-base);
  position: relative;
  overflow: hidden;
}

.result-card:hover {
  transform: translateY(-4px);
  box-shadow: var(--shadow-lg);
}

.result-card::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 4px;
  background: linear-gradient(90deg, var(--china-red) 0%, var(--primary) 50%, var(--asean) 100%);
}

.result-icon {
  font-size: 32px;
  margin-bottom: 16px;
  display: block;
}

.result-title {
  font-size: 18px;
  font-weight: 700;
  margin-bottom: 8px;
  color: var(--text-primary);
}

.result-desc {
  font-size: 14px;
  color: var(--text-secondary);
  margin-bottom: 16px;
  line-height: 1.5;
}

.result-actions {
  display: flex;
  gap: 12px;
}

.action-button {
  padding: 8px 16px;
  border: none;
  border-radius: var(--radius-md);
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: var(--transition-base);
  display: flex;
  align-items: center;
  gap: 6px;
}

.action-button.primary {
  background: var(--primary);
  color: white;
}

.action-button.secondary {
  background: var(--bg-gray-100);
  color: var(--text-primary);
}

.action-button:hover {
  transform: translateY(-1px);
  box-shadow: var(--shadow-md);
}

/* Action Buttons */
.form-actions {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 32px;
  padding-top: 24px;
  border-top: 1px solid var(--border-light);
}

.btn {
  padding: 14px 28px;
  border: none;
  border-radius: var(--radius-md);
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: var(--transition-base);
  display: inline-flex;
  align-items: center;
  gap: 8px;
}

.btn-primary {
  background: linear-gradient(135deg, var(--primary) 0%, var(--asean) 100%);
  color: white;
}

.btn-secondary {
  background: var(--bg-gray-100);
  color: var(--text-primary);
  border: 1px solid var(--border-medium);
}

.btn:hover {
  transform: translateY(-2px);
  box-shadow: var(--shadow-lg);
}

.btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
  transform: none;
  box-shadow: none;
}

/* Responsive */
@media (max-width: 1024px) {
  .main-container {
    padding: 20px;
  }

  .course-grid {
    grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
  }
}

@media (max-width: 768px) {
  .header-container {
    flex-direction: column;
    gap: 12px;
  }

  .hero-title {
    font-size: 36px;
  }

  .content-header {
    padding: 24px 20px;
  }

  .content-title {
    font-size: 24px;
  }

  .course-grid {
    grid-template-columns: 1fr;
  }

  .results-grid {
    grid-template-columns: 1fr;
  }

  .form-actions {
    flex-direction: column;
    gap: 12px;
  }

  .btn {
    width: 100%;
    justify-content: center;
  }
}
</style>